import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageSquare, Send, Loader2, ShieldAlert } from 'lucide-react';
import { listMessageThreads, getMessageThread, sendMessage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getInitials } from '../lib/utils';
import { toast } from 'sonner';

const MessagesPanel = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [activePeer, setActivePeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [blockedHint, setBlockedHint] = useState('');
  const scrollRef = useRef(null);

  const refreshThreads = useCallback(async () => {
    setLoadingThreads(true);
    try {
      const res = await listMessageThreads();
      setThreads(res.data || []);
    } catch (e) {
      setThreads([]);
    } finally {
      setLoadingThreads(false);
    }
  }, []);

  useEffect(() => { refreshThreads(); }, [refreshThreads]);

  const openThread = useCallback(async (peer) => {
    setActivePeer(peer);
    setLoadingMsgs(true);
    setBlockedHint('');
    try {
      const res = await getMessageThread(peer.peer_id);
      setMessages(res.data || []);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      setMessages([]);
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!activePeer || !draft.trim()) return;
    setSending(true);
    setBlockedHint('');
    try {
      await sendMessage(activePeer.peer_id, draft.trim());
      setDraft('');
      const res = await getMessageThread(activePeer.peer_id);
      setMessages(res.data || []);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
      refreshThreads();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send message';
      if (typeof msg === 'string' && msg.toLowerCase().includes('payments must be processed')) {
        setBlockedHint(msg);
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Could not send message');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-[#18181B] border border-zinc-800 rounded-lg overflow-hidden grid grid-cols-1 md:grid-cols-[260px_1fr] min-h-[400px]"
      data-testid="messages-panel"
    >
      {/* Threads list */}
      <aside className="border-b md:border-b-0 md:border-r border-zinc-800 max-h-[420px] overflow-y-auto">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#C0A062]" />
          <h3 className="font-semibold text-white text-sm">Messages</h3>
        </div>
        {loadingThreads ? (
          <div className="p-6 text-zinc-500 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : threads.length === 0 ? (
          <p className="p-6 text-sm text-zinc-500" data-testid="no-threads">
            No conversations yet. Message someone you&apos;ve booked with.
          </p>
        ) : (
          <ul className="divide-y divide-zinc-800" data-testid="threads-list">
            {threads.map((t) => (
              <li key={t.peer_id}>
                <button
                  type="button"
                  onClick={() => openThread(t)}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-900 transition-colors text-left ${
                    activePeer?.peer_id === t.peer_id ? 'bg-zinc-900' : ''
                  }`}
                  data-testid={`thread-${t.peer_id}`}
                >
                  <Avatar className="w-9 h-9 border border-zinc-700 shrink-0">
                    <AvatarImage src={t.peer_image} />
                    <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-[11px]">
                      {getInitials(t.peer_name || 'U')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-medium truncate">{t.peer_name}</p>
                    <p className="text-xs text-zinc-500 truncate">{t.last_message}</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* Conversation */}
      <section className="flex flex-col">
        {!activePeer ? (
          <div className="flex-1 flex items-center justify-center text-sm text-zinc-500 p-8 text-center">
            Select a conversation to start messaging.
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-3">
              <Avatar className="w-8 h-8 border border-zinc-700">
                <AvatarImage src={activePeer.peer_image} />
                <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-[10px]">
                  {getInitials(activePeer.peer_name || 'U')}
                </AvatarFallback>
              </Avatar>
              <p className="text-white font-semibold text-sm">{activePeer.peer_name}</p>
            </div>

            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3 max-h-[360px]"
              data-testid="messages-list"
            >
              {loadingMsgs ? (
                <div className="text-zinc-500 text-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : messages.length === 0 ? (
                <p className="text-sm text-zinc-500">No messages yet. Say hi.</p>
              ) : (
                messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div
                      key={m.id}
                      className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${m.id}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                          mine
                            ? 'bg-[#C0A062] text-black'
                            : 'bg-zinc-800 text-zinc-100'
                        }`}
                      >
                        {m.body}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {blockedHint && (
              <div
                className="mx-4 mb-2 p-3 rounded-md border border-red-900/60 bg-red-950/40 text-xs text-red-200 flex items-start gap-2"
                data-testid="blocked-message-warning"
              >
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{blockedHint}</span>
              </div>
            )}

            <form onSubmit={handleSend} className="border-t border-zinc-800 p-3 flex gap-2">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Type a message…"
                className="bg-[#09090B] border-zinc-800 text-white"
                disabled={sending}
                data-testid="message-input"
              />
              <Button
                type="submit"
                disabled={sending || !draft.trim()}
                className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
                data-testid="send-message-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </>
        )}
      </section>
    </div>
  );
};

export default MessagesPanel;
